import {
    DocumentVersion,
    PublicationForm,
    StisPublicationAction,
    visibilityTypeOptions,
} from '@web/vin-sessions/types';
import { useCallback, useState } from 'react';
import { getLogger } from '@packages/core/logging';
import { usePublicationsDispatch } from '../context/publications.hooks';
import { docsService } from '@web/services/singletons';
import { publicationsActions } from '../context/publications.state';
import { parseVersion } from '../version.utils';

const logger = getLogger('vin-sessions');

interface Props {
    publicationFormId: string | undefined;
    publicationAction?: StisPublicationAction;
    setCreatorUser: (
        value: React.SetStateAction<{
            firstName: string;
            lastName: string;
            name: string;
        } | null>
    ) => void;
    setUpdatedUser: (
        value: React.SetStateAction<{
            firstName: string;
            lastName: string;
            name: string;
        } | null>
    ) => void;
    setParentPublications: (value: React.SetStateAction<DocumentVersion[]>) => void;
}

const usePublicationData = ({
    publicationFormId,
    publicationAction,
    setCreatorUser,
    setUpdatedUser,
    setParentPublications,
}: Props) => {
    const [publicationData, setPublicationData] = useState<PublicationForm>();
    const [fetching, setFetching] = useState<boolean>(false);
    const publicationDispatch = usePublicationsDispatch();

    const fetchData = useCallback(async () => {
        if (!publicationFormId || publicationAction == StisPublicationAction.New) {
            return;
        }
        publicationDispatch(publicationsActions.isLoading({ loading: true }));
        try {
            await docsService.fetchPublicationFormById(publicationFormId).then(async (response) => {
                if (!response.success && response.aborted) {
                    return;
                } else if (!response.success) {
                    throw response.data;
                }
                const data = response.data;
                // Docs admin errors are plain text, which fails the JSON parse and arrives here as
                // success with an empty object. Treat a response without a document as a failure.
                if (!data?.documentId) {
                    throw new Error(`No publication was returned for [${publicationFormId}]`);
                }
                data.pubVersion = parseVersion(data.pubVersion) ?? 1;
                data.visibilityLevel = visibilityTypeOptions.find((option) => option.value === data.visibilityLevel);
                if (!data.documentUploadDetails) {
                    data.contentFile = {
                        fileName: '',
                        fileType: '',
                        createdDate: '',
                        createdTimestamp: '',
                    };
                } else if (data.documentUploadDetails !== null) {
                    const response = await docsService.getUserInfoCardsAccount(data.documentUploadDetails.uploadBy);
                    if (response.success && response.data.account) {
                        data.contentFile = {
                            fileName: data.documentUploadDetails.fileName,
                            fileType: data.documentUploadDetails.fileType || 'PDF',
                            createdDate: data.documentUploadDetails.uploadDate,
                            createdTimestamp: data.documentUploadDetails.uploadDate,
                            createdBy: response.data.account?.firstName + ' ' + response.data.account?.lastName,
                        };
                    } else {
                        data.contentFile = {
                            fileName: data.documentUploadDetails.fileName,
                            fileType: data.documentUploadDetails.fileType || 'PDF',
                            createdDate: data.documentUploadDetails.uploadDate,
                            createdTimestamp: data.documentUploadDetails.uploadDate,
                        };
                    }
                }

                if (data.createdBy) {
                    await docsService.getUserInfoCardsAccount(data.createdBy).then((response) => {
                        if (response.success && response.data.account) {
                            setCreatorUser({
                                firstName: response.data.account.firstName,
                                lastName: response.data.account.lastName,
                                name: response.data.account.name,
                            });
                        }
                        if (data.updatedBy) {
                            docsService.getUserInfoCardsAccount(data.updatedBy).then((response) => {
                                if (response.success && response.data.account) {
                                    setUpdatedUser({
                                        firstName: response.data.account.firstName,
                                        lastName: response.data.account.lastName,
                                        name: response.data.account.name,
                                    });
                                }
                            });
                        }
                    });
                }

                if (data && data.documentId && data.title !== 'Untitled') {
                    const response = await docsService.getPublicationVersions(data.documentId);
                    if (response && response.success) {
                        const versions = (response.data as unknown as Array<DocumentVersion>) || [];
                        if (versions) setParentPublications(versions);
                    }
                }

                // Fetch tags for the publication
                if (data && data.documentId) {
                    try {
                        const tagsResponse = await docsService.getTags({
                            documentId: data.documentId,
                        });
                        if (tagsResponse.success && tagsResponse.data && tagsResponse.data.length > 0) {
                            const tags = tagsResponse.data[0];
                            data.keywords = tags.keyWords || [];
                            data.dtcCodes = tags.dtcCodes || [];
                            data.toolNumbers = tags.toolNumbers || [];
                        }
                    } catch (error) {
                        logger.error(`Failed to fetch the tags for the publication [${data.documentId}]`, {
                            error,
                            documentId: data.documentId,
                        });
                    }
                }

                setPublicationData(data);
            });
            publicationDispatch(publicationsActions.isLoading({ loading: false }));
        } catch (e) {
            const message = e instanceof Error ? e.message : 'Failed to fetch publication by form by id';
            logger.error(`Failed to fetch the publication form [${publicationFormId}]`, {
                error: e,
                publicationFormId,
                reason: message,
            });
            publicationDispatch(publicationsActions.isLoading({ loading: false }));
            publicationDispatch(
                publicationsActions.setToastMessage({
                    toast: {
                        toastMessage: 'Failed to load the publication',
                        iconColor: 'white',
                        iconName: 'x-close',
                    },
                })
            );
        }
    }, [publicationAction, publicationFormId]);

    return {
        publicationData,
        fetching,
        actions: {
            fetchData,
        },
    };
};

export type UsePublicationFormReturnType = ReturnType<typeof usePublicationData>;

export default usePublicationData;
